import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { adminGetPromos, adminCreatePromo, adminUpdatePromo, adminDeletePromo } from "@/lib/adminApi";

const EMPTY = { code: "", type: "percent", value: 10, min_amount: 0, active: true, expires_at: "" };

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); adminGetPromos().then(setPromos).finally(() => setLoading(false)); };
  useEffect(load, []);

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const payload = { ...editing, value: parseFloat(editing.value) || 0, min_amount: parseFloat(editing.min_amount) || 0, expires_at: editing.expires_at || null };
      if (isNew) await adminCreatePromo(payload);
      else await adminUpdatePromo(editing.id, payload);
      setEditing(null); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const remove = async (p) => { if (window.confirm(`Supprimer le code ${p.code} ?`)) { await adminDeletePromo(p.id); load(); } };

  return (
    <div data-testid="admin-promos">
      <div className="admin-topbar" style={{ marginBottom: "1.5rem" }}>
        <h1 className="admin-title">Codes promo</h1>
        <button className="adm-btn" onClick={() => { setEditing({ ...EMPTY }); setIsNew(true); setErr(""); }} data-testid="promo-new"><Plus size={15} /> Nouveau code</button>
      </div>

      {loading ? <div className="adm-loader"><div className="spinner" /></div> : (
        <table className="admin-table" data-testid="promos-table">
          <thead><tr><th>Code</th><th>Type</th><th>Valeur</th><th>Min. panier</th><th>Expire</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {promos.length === 0 ? <tr><td colSpan="7" className="adm-hint" style={{ padding: "2rem", textAlign: "center" }}>Aucun code promo. Créez-en un (ex. NEROLI10 = -10%).</td></tr> : null}
            {promos.map((p) => (
              <tr key={p.id} data-testid={`promo-row-${p.code}`}>
                <td><strong style={{ color: "var(--emerald)", letterSpacing: "0.05em" }}>{p.code}</strong></td>
                <td>{p.type === "percent" ? "Pourcentage" : "Montant fixe"}</td>
                <td>{p.type === "percent" ? `${p.value}%` : `€${p.value}`}</td>
                <td>{p.min_amount ? `€${p.min_amount}` : "—"}</td>
                <td>{p.expires_at ? new Date(p.expires_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td>{p.active ? <span className="pill green">Actif</span> : <span className="pill gray">Inactif</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="icon-btn" onClick={() => { setEditing({ ...p, expires_at: p.expires_at ? p.expires_at.slice(0, 10) : "" }); setIsNew(false); setErr(""); }}><Pencil size={15} /></button>
                  <button className="icon-btn danger" onClick={() => remove(p)} data-testid={`promo-delete-${p.code}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing ? (
        <div className="adm-overlay" onClick={() => setEditing(null)}>
          <div className="adm-drawer" onClick={(e) => e.stopPropagation()} data-testid="promo-drawer" style={{ width: 440 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2>{isNew ? "Nouveau code" : "Modifier le code"}</h2>
              <button className="icon-btn" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            {err ? <div className="adm-msg error">{err}</div> : null}
            <div className="adm-form">
              <div className="adm-field"><label>Code</label><input value={editing.code} onChange={(e) => setField("code", e.target.value.toUpperCase())} data-testid="promof-code" /></div>
              <div className="adm-row">
                <div className="adm-field"><label>Type</label>
                  <select value={editing.type} onChange={(e) => setField("type", e.target.value)} data-testid="promof-type">
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                <div className="adm-field"><label>Valeur</label><input type="number" step="0.01" value={editing.value} onChange={(e) => setField("value", e.target.value)} data-testid="promof-value" /></div>
              </div>
              <div className="adm-row">
                <div className="adm-field"><label>Minimum panier (€)</label><input type="number" step="0.01" value={editing.min_amount} onChange={(e) => setField("min_amount", e.target.value)} /></div>
                <div className="adm-field"><label>Date d'expiration</label><input type="date" value={editing.expires_at || ""} onChange={(e) => setField("expires_at", e.target.value)} /></div>
              </div>
              <div className="adm-field" style={{ flexDirection: "row", alignItems: "center", gap: "0.6rem" }}>
                <input type="checkbox" checked={editing.active} onChange={(e) => setField("active", e.target.checked)} id="promof-active" style={{ width: "auto" }} />
                <label htmlFor="promof-active" style={{ margin: 0 }}>Code actif</label>
              </div>
            </div>
            <div className="adm-drawer-actions">
              <button className="adm-btn" onClick={save} disabled={saving} data-testid="promo-save">{saving ? "…" : "Enregistrer"}</button>
              <button className="adm-btn ghost" onClick={() => setEditing(null)}>Annuler</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
